import { useState, useEffect } from 'react';
import { templateApi } from '../services/api';
import '../styles/Panel.css';
import './TemplateValidationPanel.css';

function TemplateValidationPanel() {
  const [templateTypes, setTemplateTypes] = useState([]);
  const [selectedTemplateType, setSelectedTemplateType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [activeTab, setActiveTab] = useState('validate'); // 'validate' or 'generate'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(null);

  useEffect(() => {
    fetchTemplateTypes();
  }, []);

  const fetchTemplateTypes = async () => {
    try {
      const data = await templateApi.getTemplateTypes();
      setTemplateTypes(data.template_types || []);
      if (data.template_types && data.template_types.length > 0) {
        setSelectedTemplateType(data.template_types[0].value);
      }
    } catch (err) {
      console.error('Failed to fetch template types:', err);
      setError('Failed to load template types: ' + err.message);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      setValidationResult(null);
      setGenerationStatus(null);
    }
  };

  const handleValidate = async () => {
    if (!selectedFile) {
      setError('Please select a file to validate');
      return;
    }

    setLoading(true);
    setError(null);
    setValidationResult(null);

    try {
      const result = await templateApi.verifyTemplate(selectedFile, selectedTemplateType);
      setValidationResult(result);
    } catch (err) {
      console.error('Validation failed:', err);
      setError('Validation failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedFile) {
      setError('Please select a source data file');
      return;
    }

    setLoading(true);
    setError(null);
    setGenerationStatus(null);

    try {
      const blob = await templateApi.generateTemplate(selectedFile, selectedTemplateType);

      // Download the generated file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedTemplateType}_template_filled.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setGenerationStatus({
        success: true,
        message: 'Template generated successfully!'
      });
    } catch (err) {
      console.error('Generation failed:', err);

      // Handle error response that might contain JSON
      if (err.isApiError && err.data) {
        setGenerationStatus({
          success: false,
          message: err.data.message || 'Generation failed',
          missing_required_fields: err.data.missing_required_fields || [],
          mapping_summary: err.data.mapping_summary
        });
      } else {
        setError('Generation failed: ' + (err.message || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const renderValidationResults = () => {
    if (!validationResult) return null;

    const { valid, errors, warnings, summary } = validationResult;

    return (
      <div className="validation-results">
        <div className={`validation-status ${valid ? 'valid' : 'invalid'}`}>
          <h3>{valid ? '✓ Validation Passed' : '✗ Validation Failed'}</h3>
        </div>

        {summary && (
          <div className="validation-summary">
            <h4>Summary</h4>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="label">Total Errors:</span>
                <span className={`value ${summary.total_errors > 0 ? 'error' : ''}`}>
                  {summary.total_errors}
                </span>
              </div>
              <div className="summary-item">
                <span className="label">Total Warnings:</span>
                <span className={`value ${summary.total_warnings > 0 ? 'warning' : ''}`}>
                  {summary.total_warnings}
                </span>
              </div>
              <div className="summary-item">
                <span className="label">Sheets Validated:</span>
                <span className="value">{summary.sheets_validated}</span>
              </div>
              <div className="summary-item">
                <span className="label">Validation Time:</span>
                <span className="value">{summary.validation_time?.toFixed(2)}s</span>
              </div>
            </div>
          </div>
        )}

        {errors && errors.length > 0 && (
          <div className="validation-errors">
            <h4>Errors ({errors.length})</h4>
            <div className="error-list">
              {errors.map((error, index) => (
                <div key={index} className="error-item">
                  <div className="error-location">
                    <span className="sheet">{error.sheet}</span>
                    {error.row && <span className="row">Row {error.row}</span>}
                    {error.column && <span className="column">Col {error.column}</span>}
                    {error.column_name && <span className="column-name">({error.column_name})</span>}
                  </div>
                  <div className="error-message">{error.message}</div>
                  <div className="error-details">
                    <span>Expected: {error.expected}</span>
                    <span>Found: {error.found}</span>
                    {error.value !== null && <span>Value: {String(error.value)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {warnings && warnings.length > 0 && (
          <div className="validation-warnings">
            <h4>Warnings ({warnings.length})</h4>
            <div className="warning-list">
              {warnings.map((warning, index) => (
                <div key={index} className="warning-item">
                  <div className="warning-location">
                    <span className="sheet">{warning.sheet}</span>
                    {warning.row && <span className="row">Row {warning.row}</span>}
                    {warning.column && <span className="column">Col {warning.column}</span>}
                    {warning.column_name && <span className="column-name">({warning.column_name})</span>}
                  </div>
                  <div className="warning-message">{warning.message}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderGenerationStatus = () => {
    if (!generationStatus) return null;

    const { success, message, missing_required_fields, mapping_summary } = generationStatus;

    return (
      <div className="generation-status">
        <div className={`status-header ${success ? 'success' : 'failure'}`}>
          <h3>{success ? '✓ Generation Successful' : '✗ Generation Failed'}</h3>
          <p>{message}</p>
        </div>

        {!success && missing_required_fields && missing_required_fields.length > 0 && (
          <div className="missing-fields">
            <h4>Missing Required Fields</h4>
            <ul>
              {missing_required_fields.map((field, index) => (
                <li key={index}>{field}</li>
              ))}
            </ul>
          </div>
        )}

        {mapping_summary && (
          <div className="mapping-summary">
            <h4>Mapping Summary</h4>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="label">Source Columns:</span>
                <span className="value">{mapping_summary.total_source_columns}</span>
              </div>
              <div className="summary-item">
                <span className="label">Template Fields:</span>
                <span className="value">{mapping_summary.total_template_fields}</span>
              </div>
              <div className="summary-item">
                <span className="label">Mapped Fields:</span>
                <span className="value">{mapping_summary.mapped_fields}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="panel template-validation-panel">
      <h2>Template Validation & Generation</h2>
      <p className="panel-description">
        Validate Excel templates against predefined schemas or generate filled templates from source data using AI
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Tab Navigation */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'validate' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('validate');
            setError(null);
            setValidationResult(null);
            setGenerationStatus(null);
          }}
        >
          Validate Template
        </button>
        <button
          className={`tab ${activeTab === 'generate' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('generate');
            setError(null);
            setValidationResult(null);
            setGenerationStatus(null);
          }}
        >
          Generate Template
        </button>
      </div>

      {/* Template Type Selector */}
      <div className="form-group">
        <label htmlFor="templateType">Template Type:</label>
        <select
          id="templateType"
          value={selectedTemplateType}
          onChange={(e) => setSelectedTemplateType(e.target.value)}
          className="form-control"
          disabled={loading}
        >
          {templateTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.description} ({type.value.toUpperCase()})
            </option>
          ))}
        </select>
      </div>

      {/* File Upload */}
      <div className="form-group">
        <label htmlFor="fileUpload">
          {activeTab === 'validate' ? 'Select Template File:' : 'Select Source Data File:'}
        </label>
        <input
          id="fileUpload"
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          className="form-control"
          disabled={loading}
        />
        {selectedFile && (
          <div className="file-info">
            Selected: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(2)} KB)
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="button-group">
        {activeTab === 'validate' ? (
          <button
            onClick={handleValidate}
            disabled={!selectedFile || loading}
            className="btn btn-primary"
          >
            {loading ? 'Validating...' : 'Validate Template'}
          </button>
        ) : (
          <button
            onClick={handleGenerate}
            disabled={!selectedFile || loading}
            className="btn btn-primary"
          >
            {loading ? 'Generating...' : 'Generate Template'}
          </button>
        )}

        {selectedFile && !loading && (
          <button
            onClick={() => {
              setSelectedFile(null);
              setValidationResult(null);
              setGenerationStatus(null);
              setError(null);
              document.getElementById('fileUpload').value = '';
            }}
            className="btn btn-secondary"
          >
            Clear
          </button>
        )}
      </div>

      {/* Results */}
      {activeTab === 'validate' && renderValidationResults()}
      {activeTab === 'generate' && renderGenerationStatus()}
    </div>
  );
}

export default TemplateValidationPanel;
